/**
 * The strip across the top of Home: my week in one number, where I stand, the floor's goal against
 * the pace the week says it should be at, and the streak. Dark so it reads as the scoreboard, one
 * accent on it, and every reading in words beside its bar.
 */
import { toUsDate, type Cockpit, type HomeDashboard } from "@tlcrm/shared";

import { PaceBar, PaceMark, StreakStrip } from "../../components/kit";
import { paceOf } from "../../lib/cockpit";
import { compactMoney, percent } from "../../lib/chart";
import { money } from "../../lib/format";

function greeting(now: number): string {
  const h = new Date(now).getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

export function Hero({
  firstName,
  now,
  cockpit,
  company,
}: {
  firstName: string;
  now: number;
  cockpit: Cockpit;
  company: HomeDashboard["company"] | undefined;
}) {
  const { me, streak, week, elapsed } = cockpit;
  const daysLeft = Math.max(0, Math.ceil((1 - elapsed) * 7 - 0.01));
  const next = me.rank && me.rank > 1 ? cockpit.leaders.find((l) => l.rank === me.rank! - 1) : null;
  const behind = me.rank === 1 ? cockpit.leaders.find((l) => l.rank === 2) : null;
  const pace = company ? paceOf(company.premium, company.goal, elapsed) : null;
  const atRisk = streak.current > 0 && !streak.thisWeekSold;

  return (
    <section className="hero" aria-label="My week">
      <div className="hero-me">
        <p className="hero-line">
          {greeting(now)}, {firstName} ·{" "}
          {new Date(now).toLocaleDateString("en-US", { weekday: "long" })}{" "}
          <span className="mono">{toUsDate(new Date(now).toLocaleDateString("en-CA"))}</span> ·{" "}
          {daysLeft === 0
            ? "last day of the week"
            : `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`}
        </p>
        <div className="hero-total">
          <span className="mono">{money(me.premium)}</span>
          <span>
            Closed Won this week · {me.count} {me.count === 1 ? "policy" : "policies"}
          </span>
        </div>
        <div className="hero-chips">
          {me.rank ? (
            <span className={`hero-chip${me.moved > 0 ? " is-accent" : ""}`}>
              #{me.rank} of {me.of}
              {me.moved > 0 && ` · ▲ ${me.moved} since yesterday`}
              {me.moved < 0 && ` · ▼ ${-me.moved} since yesterday`}
            </span>
          ) : (
            <span className="hero-chip">Not on the board yet this week</span>
          )}
          {next && me.gapToNext !== null && (
            <span className="hero-chip">
              <span className="mono">{money(me.gapToNext)}</span> to #{next.rank}
            </span>
          )}
          {behind && (
            <span className="hero-chip">
              Leading by <span className="mono">{money(me.premium - behind.premium)}</span>
            </span>
          )}
          {!me.rank && cockpit.leaders.length > 0 && (
            <span className="hero-chip">
              <span className="mono">
                {money(cockpit.leaders[cockpit.leaders.length - 1]!.premium + 0.01)}
              </span>{" "}
              gets you on it
            </span>
          )}
        </div>
      </div>

      <div className="hero-block">
        <div className="hero-k">
          <span>Company goal</span>
          {company ? (
            <span className="mono">
              {compactMoney(company.premium)} / {compactMoney(company.goal)}
            </span>
          ) : (
            <span>–</span>
          )}
        </div>
        <PaceBar
          value={company?.premium ?? 0}
          goal={company?.goal ?? 0}
          elapsed={elapsed}
          label="Company premium against this week's goal"
          onDark
        />
        <div className="hero-k is-sub">
          <span>
            {company && company.goal
              ? `${percent(Math.min(9.99, company.premium / company.goal))} done · pace ${percent(elapsed)}`
              : "No goal set for this week"}
          </span>
          {pace && company?.goal ? <PaceMark pace={pace} /> : null}
        </div>
        <p className="hero-note">
          Week of <span className="mono">{toUsDate(week.start)}</span> to{" "}
          <span className="mono">{toUsDate(week.end)}</span>
        </p>
      </div>

      <div className="hero-block">
        <div className="hero-k">
          <span>Selling streak</span>
          <span>
            <b className="mono">{streak.current}</b> {streak.current === 1 ? "week" : "weeks"} in a
            row
          </span>
        </div>
        <StreakStrip weeks={streak.weeks} thisWeekSold={streak.thisWeekSold} />
        <div className="hero-k is-sub">
          <span>
            Best run <b className="mono">{streak.best}</b> {streak.best === 1 ? "week" : "weeks"}
          </span>
          {atRisk ? (
            <span className="mark mark-ochre">Sell by {dayName(week.end)} to keep it</span>
          ) : streak.thisWeekSold ? (
            <span className="mark mark-accent">This week counts</span>
          ) : (
            <span className="t-hero">One sale starts it</span>
          )}
        </div>
        <p className="hero-note">A week counts with one Closed Won in it.</p>
      </div>
    </section>
  );
}

function dayName(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString("en-US", { weekday: "long" });
}
